<script context="module" lang="ts">
  export type Row = {
    [key: string]: string | number;
  };

  export type Column = {
    label: string;
    sortable: boolean;
    field?: string;
    formatter?: (rawFieldContent: string | number) => any;
  };

  export type SortDirection = 'ASC' | 'DESC';
</script>

<script lang="ts">
  import Icon from './Icon.svelte';

  export let rows: Row[] = [];
  export let columns: Column[];
  export let sortBy: string | undefined = undefined;
  export let sortDirection: SortDirection = 'ASC';

  const isColumnSortable = (column: Column) => column.sortable && column.field;

  const getFieldContent = (row: Row, column: Column) => {
    if (!column.field) {
      return '';
    }

    return column.formatter ? column.formatter(row[column.field]) : row[column.field];
  };

  const onTableHeadClick = (column: Column) => {
    if (isColumnSortable(column)) {
      sortBy = column.field;
      if (sortBy === column.field) {
        sortDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC';
      } else {
        sortDirection = 'DESC';
      }
    }
  };
</script>

<div class="table-wrapper">
  <table class="table">
    <thead class="table-head">
      {#each columns as column}
        <th
          class="table-head-cell {column.field === sortBy ? sortDirection.toLowerCase() : ''}"
          class:sortable={isColumnSortable(column)}
          on:click={() => onTableHeadClick(column)}
        >
          <span>
            {column.label}

            {#if isColumnSortable(column)}
              <span class="sort-arrow">
                <Icon variant="arrowUp" />
              </span>
            {/if}
          </span>
        </th>
      {/each}
    </thead>
    <tbody class="table-body">
      {#each rows as row}
        <tr>
          {#each columns as column}
            <td>{getFieldContent(row, column)}</td>
          {/each}
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<style lang="scss">
  @use '../styles/colors';
  @use '../styles/spacing';
  @use '../styles/font-sizes';

  .table-wrapper {
    display: block;
    border: 1px solid colors.$black10;
    border-radius: 4px;
  }

  .table {
    width: 100%;
    text-align: left;
    border-collapse: collapse;
  }

  .table-head {
    background-color: colors.$whiteHover;
    border-bottom: 1px solid colors.$black10;
  }

  .table-head-cell {
    padding: spacing.$xs;
    font-size: font-sizes.$text-s;
    color: colors.$black60;
    line-height: 1.42;
    vertical-align: middle;

    &:first-child {
      padding-left: spacing.$xs;
    }

    &:last-child {
      padding-right: spacing.$xs;
    }

    &.sortable:hover {
      cursor: pointer;

      .sort-arrow {
        visibility: visible;
      }
    }

    & > span {
      display: inline-flex;
      justify-content: center;
      align-items: center;
    }

    .sort-arrow {
      visibility: hidden;
      transition: transform 250ms ease-in;
      transform: rotateX(0deg);
    }

    &.asc,
    &.desc {
      .sort-arrow {
        visibility: visible;
      }
    }

    &.asc {
      .sort-arrow {
        transform: rotateX(180deg);
      }
    }
  }

  .table-body {
    tr:not(:last-of-type) {
      border-bottom: 1px solid colors.$black10;
    }

    tr:hover {
      background-color: colors.$black04;
    }
    td {
      font-size: font-sizes.$text-m;
      line-height: 1.25;
      padding: spacing.$s spacing.$xs;
      &:first-child {
        padding-left: spacing.$s;
      }
      &:last-child {
        padding-right: spacing.$s;
      }
    }
  }
</style>
